import { useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { v4 } from 'uuid';
import dynamic from 'next/dynamic';
import tailwindConfig from '../../../../tailwind.config';
import type { RoomAndGuestCount } from '../../booking/meal-plan/types';
import { getTotalChildCount, getTotalInfantCount } from '../../common/GuestCounter/Counter';
import GuestsOutline from '../../common/Icons/GuestsOutline';
import { SearchStatus } from '../../../core/constants';
import SkipTranslationText from '../../common/SkipTranslationText';

const Counter = dynamic(() => import('../../common/GuestCounter/Counter'));

type Props = {
  roomAndGuestCount: RoomAndGuestCount;
  setRoomAndGuestCount: (guestCount: RoomAndGuestCount) => void;
  searchStatus?: SearchStatus;
  setSearchStatus: (status: SearchStatus) => void;
};

const GuestCounter = ({
  roomAndGuestCount,
  setRoomAndGuestCount,
  searchStatus,
  setSearchStatus,
}: Props) => {
  const { t } = useTranslation();
  const [id] = useState(v4());
  const [tempGuestCount, setTempGuestCount] = useState(roomAndGuestCount);

  const setCountTempGuestCount = (operation: 'add' | 'remove', key: keyof RoomAndGuestCount) => {
    const count = {
      ...tempGuestCount,
      [key]: operation === 'add' ? tempGuestCount[key] + 1 : tempGuestCount[key] - 1,
    };
    setTempGuestCount(count);
    setRoomAndGuestCount(count);
  };

  useEffect(() => {
    setTempGuestCount(roomAndGuestCount);
  }, [roomAndGuestCount]);

  const events = {
    onToggle: () => {
      setSearchStatus(SearchStatus.GUEST);
      setRoomAndGuestCount(tempGuestCount);
    },
  };

  return (
    <div>
      {searchStatus === SearchStatus.GUEST ? (
        <div className="flex h-full flex-col rounded-3xl bg-white p-4">
          <div className="flex h-full flex-col border-b-2 border-dotted border-gray-200 pb-4">
            <div className="mb-4 flex h-7 w-full items-center justify-center">
              <p className="text-xl font-bold">{t('component:guest_counter.modal.title')}</p>
            </div>
            <div className="flex h-14 items-center justify-between">
              <p className="text-regular">{t('hotel:availability.guest_counter.room')}</p>
              <Counter
                roundedSearchBar
                count={tempGuestCount}
                guestType="roomCount"
                setCount={setCountTempGuestCount}
              />
            </div>
            <div className="flex h-14 items-center justify-between">
              <p className="text-regular">{t('hotel:availability.guest_counter.adult')}</p>
              <Counter
                roundedSearchBar
                count={tempGuestCount}
                guestType="adultCount"
                setCount={setCountTempGuestCount}
              />
            </div>
            <div className="flex h-14 items-center justify-between">
              <p className="text-regular">
                {t('component:child_counter_accordion.elementary_school_child')}
              </p>
              <Counter
                roundedSearchBar
                count={tempGuestCount}
                guestType="seniorElementarySchoolChildCount"
                setCount={setCountTempGuestCount}
              />
            </div>
          </div>
          <div className="flex flex-col pt-4">
            <div className="flex h-14 items-center justify-between">
              <p className="text-regular">{t('component:child_counter_accordion.infant')}</p>
              <div className="w-[123px] text-center">
                <p className="text-xl font-bold">{getTotalInfantCount(tempGuestCount)}</p>
              </div>
            </div>
            <div className="flex h-14 items-center justify-between">
              <p className="text-regular">
                {t('component:child_counter_accordion.with_meal_futon')}
              </p>
              <Counter
                roundedSearchBar
                count={tempGuestCount}
                guestType="infantWithMealAndFutonCount"
                setCount={setCountTempGuestCount}
              />
            </div>
            <div className="flex h-14 items-center justify-between">
              <p className="text-regular">{t('component:child_counter_accordion.with_futon')}</p>
              <Counter
                roundedSearchBar
                count={tempGuestCount}
                guestType="infantWithFutonCount"
                setCount={setCountTempGuestCount}
              />
            </div>
            <div className="flex h-14 items-center justify-between">
              <p className="text-regular">
                {t('component:child_counter_accordion.without_meal_futon')}
              </p>
              <Counter
                roundedSearchBar
                count={tempGuestCount}
                guestType="infantWithoutMealAndFutonCount"
                setCount={setCountTempGuestCount}
              />
            </div>
          </div>
        </div>
      ) : (
        <div className="h-14 rounded-full bg-white">
          <button
            id={`counter-field-${id}`}
            type="button"
            className="flex size-full cursor-pointer items-center gap-2 rounded-full px-5 text-left"
            onClick={events.onToggle}
          >
            <GuestsOutline size={24} color={tailwindConfig.theme.colors.orange[700]} />
            <p className="truncate">
              <SkipTranslationText
                i18nKey="hotel:availability.guest_counter.total_room_and_guest_count"
                values={{
                  adultGuestCount: tempGuestCount.adultCount * tempGuestCount.roomCount,
                  childGuestCount: getTotalChildCount(tempGuestCount) * tempGuestCount.roomCount,
                  roomCount: tempGuestCount.roomCount,
                }}
              />
            </p>
          </button>
        </div>
      )}
    </div>
  );
};

export default GuestCounter;
